/* eslint-disable jsx-a11y/alt-text */
import { FC, MouseEventHandler, ReactNode, useState } from 'react';

import { IconType } from 'react-icons';

import Link from 'next/link';
import Image, { StaticImageData } from 'next/image';

import { TColorProp, useTheme } from 'styled-components';
import { Tooltip } from '@mui/material';

import { usePrivateLayout } from '~/common/providers/private-layout';

import { ButtonContainer, LoaderWrapper } from './styles';
import { Typography } from '../Typography';
import { Loader } from '../Loader';

type TButtonsHeight = '48' | '40' | '36' | '32' | '28' | '24' | '20' | '16';

export interface IButtonProps {
  'aria-label'?: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  width?: string;
  id?: string;
  circular?: boolean;
  backgroundColor?: string;
  textOrIconColor?: string;
  textOrIconColorOnHover?: string;
  borderColor?: string;
  customCssBorderHover?: string;
  withArrowLeft?: boolean;
  withArrowRight?: boolean;
  disabled?: boolean;
  height?: TButtonsHeight;
  iconSize?: number | string;
  icon?: IconType;
  image?: string | StaticImageData;
  text?: string;
  tooltip?: ReactNode;
  justifyContent?: string;
  href?: string;
  loading?: boolean;
  loaderColor?: TColorProp;
  containerPadding?: number;
  boxShadow?: string;
  type?: 'button' | 'reset' | 'submit';
  loader?: 'puff' | 'bounce' | 'clip' | 'scale' | 'bar';
}

export const Button: FC<IButtonProps> = ({
  'aria-label': ariaLabel,
  id,
  onClick = () => null,
  width = 'min-content',
  image,
  loading = false,
  containerPadding = 10,
  boxShadow,
  backgroundColor,
  textOrIconColor,
  textOrIconColorOnHover,
  borderColor,
  customCssBorderHover,
  circular = false,
  disabled = false,
  type = 'button',
  justifyContent = 'space-between',
  height = '40',
  iconSize,
  icon: Icon,
  text,
  href,
  tooltip,
  loader = 'bar',
  loaderColor = 'primary',
}) => {
  const theme = useTheme();
  const { isMobile } = usePrivateLayout();
  const [hover, setHover] = useState<boolean>(false);
  const isDisabled = disabled || loading;
  const resolvedBackgroundColor = backgroundColor || theme.primary;
  const resolvedTextOrIconColor = isDisabled
    ? theme.neutral['500']
    : textOrIconColor || theme.neutral[0];
  const resolvedTextOrIconColorOnHover = isDisabled
    ? resolvedTextOrIconColor
    : textOrIconColorOnHover || resolvedTextOrIconColor;
  const resolvedBorderColor = isDisabled
    ? theme.neutral['500']
    : borderColor || theme.primary;

  const Element = (
    <ButtonContainer
      aria-label={ariaLabel}
      type={type}
      id={id}
      circular={circular}
      boxShadow={boxShadow}
      containerPadding={containerPadding}
      width={width}
      backgroundColor={resolvedBackgroundColor}
      textOrIconColorOnHover={resolvedTextOrIconColorOnHover}
      textColor={resolvedTextOrIconColor}
      borderColor={resolvedBorderColor}
      disabled={isDisabled}
      customCssBorderHover={customCssBorderHover}
      justifyContent={justifyContent}
      height={`${height}px`}
      onClick={isDisabled ? undefined : onClick}
      onMouseOver={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        maxHeight: `${height}px !important`,
        minHeight: `${height}px !important`,
        maxWidth:
          width === 'min-content'
            ? 'max-content !important'
            : `${width} !important`,
        minWidth:
          width === 'min-content'
            ? 'max-content !important'
            : `${width} !important`,
      }}
    >
      {!loading ? (
        <>
          {Icon && (
            <Icon
              color={
                hover ? resolvedTextOrIconColorOnHover : resolvedTextOrIconColor
              }
              size={iconSize}
            />
          )}
          {image && <Image alt="img" src={image} width={20} height={20} />}
          {text && <Typography.Label size="medium">{text}</Typography.Label>}
        </>
      ) : (
        <LoaderWrapper>
          <Loader size={Number(height) - 6} type={loader} color={loaderColor} />
        </LoaderWrapper>
      )}
    </ButtonContainer>
  );

  const WithOrWithoutTooltip =
    tooltip && !isMobile ? (
      <Tooltip title={tooltip} arrow>
        <span style={{ display: 'inline-flex', width: width || 'auto' }}>
          {Element}
        </span>
      </Tooltip>
    ) : (
      Element
    );

  return (
    <>
      {href ? (
        <>
          <Link href={href} passHref>
            {WithOrWithoutTooltip}
          </Link>
        </>
      ) : (
        <>{WithOrWithoutTooltip}</>
      )}
    </>
  );
};
